import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Path, Circle, Text as SvgText, TSpan } from 'react-native-svg';
import { ChartConfig } from '../types';

interface PieChartData {
  name: string;
  value: number;
  color: string;
  legendFontColor: string;
  legendFontSize: number;
}

interface PieChartProps {
  data: PieChartData[];
  width: number;
  height: number;
  chartConfig: ChartConfig;
  accessor: string;
  backgroundColor?: string;
  paddingLeft?: string;
  center?: [number, number];
  absolute?: boolean;
  hasLegend?: boolean;
  legendPosition?: 'top' | 'bottom';
  showLabels?: boolean;
  showValues?: boolean;
  showPercentage?: boolean;
  labelColor?: string;
  labelsOutside?: boolean;
  onDataPointClick?: (data: any, index: number) => void;
  isZeroValues?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  accessor,
  center = [0, 0],
  hasLegend = true,
  legendPosition = 'top',
  showLabels = true,
  showValues = false,
  showPercentage = true,
  labelColor = '#000000',
  labelsOutside = false,
  onDataPointClick,
  isZeroValues = false,
}) => {

  const hasValidData = data && Array.isArray(data) && data.length > 0;

  if (!hasValidData) {
    return (
      <View style={{ width, height, backgroundColor: chartConfig.backgroundColor }}>
      </View>
    );
  }

  const chartData = data;

  const itemsPerRow = Math.floor(width / 80);
  const rows = hasLegend ? Math.ceil(chartData.length / Math.max(itemsPerRow, 1)) : 0;
  const legendHeight = hasLegend ? Math.min(rows * 20 + 12, 60) : 0;
  const chartAreaHeight = height - legendHeight;
  const maxRadius = Math.min(width, chartAreaHeight) / 2.5;
  const radius = maxRadius;

  const centerX = width / 2;
  const centerY = chartAreaHeight / 2;

  const total = chartData.reduce((sum, item) => sum + item.value, 0) || 1;
  let currentAngle = -Math.PI / 2;

  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerRadius = 0
  ) => {
    const x1 = centerX + Math.cos(startAngle) * radius;
    const y1 = centerY + Math.sin(startAngle) * radius;
    const x2 = centerX + Math.cos(endAngle) * radius;
    const y2 = centerY + Math.sin(endAngle) * radius;

    const largeArcFlag = Math.abs(endAngle - startAngle) <= Math.PI ? '0' : '1';
    const sweepFlag = endAngle < startAngle ? '0' : '1';

    return [
      'M',
      centerX,
      centerY,
      'L',
      x1,
      y1,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      sweepFlag,
      x2,
      y2,
      'Z',
    ].join(' ');
  };

  const renderLegend = () => {
    if (!hasLegend) return null;

    return (
      <View style={{
        paddingHorizontal: 12,
        paddingVertical: 6,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {chartData.map((item, index) => (
          <View
            key={index}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: 6,
              marginBottom: 6,
            }}
          >
            <View
              style={{
                width: 9,
                height: 9,
                backgroundColor: item.color,
                marginRight: 4,
                borderRadius: 1.5,
              }}
            />
            <Text
              style={{
                color: item.legendFontColor,
                fontSize: 12,
              }}
            >
              {item.name}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={{ backgroundColor: chartConfig.backgroundColor }}>
      {legendPosition === 'top' && renderLegend()}
      <Svg width={width} height={chartAreaHeight}>
        {chartData.length === 1 ? (
          (() => {
            const item = chartData[0];
            const percentage = '100.0';

            const hasPercentage = showLabels && showPercentage;
            const hasValue = showLabels && showValues;

            return (
              <React.Fragment key={0}>
                <Circle
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill={item.color}
                  onPress={() => onDataPointClick?.(item, 0)}
                />
                {showLabels && (hasPercentage || hasValue || item.name) && (
                  <SvgText
                    x={centerX}
                    y={centerY}
                    fill={labelColor}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {hasPercentage && hasValue ? (
                      <>
                        <TSpan x={centerX} dy="0">{percentage}%</TSpan>
                        <TSpan x={centerX} dy="14">{item.value}</TSpan>
                      </>
                    ) : hasPercentage ? (
                      `${percentage}%`
                    ) : hasValue ? (
                      `${item.value}`
                    ) : (
                      item.name
                    )}
                  </SvgText>
                )}
              </React.Fragment>
            );
          })()
        ) : (
          chartData.map((item, index) => {
            const angle = isZeroValues
              ? (2 * Math.PI) / chartData.length
              : (item.value / total) * 2 * Math.PI;
            const endAngle = currentAngle + angle;
            const path = createArcPath(currentAngle, endAngle);
            const midAngle = currentAngle + angle / 2;
            const labelRadius = labelsOutside ? radius * 1.15 : radius * 0.7;
            const labelX = centerX + Math.cos(midAngle) * labelRadius;
            const labelY = centerY + Math.sin(midAngle) * labelRadius;
            const percentage = isZeroValues
              ? (100 / chartData.length).toFixed(1)
              : ((item.value / total) * 100).toFixed(1);
            const hasPercentage = showLabels && showPercentage;
            const hasValue = showLabels && showValues;

            const result = (
              <React.Fragment key={index}>
                <Path
                  d={path}
                  fill={item.color}
                  onPress={() => onDataPointClick?.(item, index)}
                />
                {showLabels && (hasPercentage || hasValue || item.name) && (
                  <SvgText
                    x={labelX}
                    y={labelY}
                    fill={labelColor}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {hasPercentage && hasValue ? (
                      <>
                        <TSpan x={labelX} dy="0">{percentage}%</TSpan>
                        <TSpan x={labelX} dy="14">{item.value}</TSpan>
                      </>
                    ) : hasPercentage ? (
                      `${percentage}%`
                    ) : hasValue ? (
                      `${item.value}`
                    ) : (
                      item.name
                    )}
                  </SvgText>
                )}
              </React.Fragment>
            );

            currentAngle = endAngle;
            return result;
          })
        )}
      </Svg>
      {legendPosition === 'bottom' && renderLegend()}
    </View>
  );
};

export default PieChart;
